import React, { useCallback, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  ArrowLeft,
  Edit,
  ToggleLeft,
  ToggleRight,
  Tag,
  MapPin,
  Car,
  Calendar,
  DollarSign,
  Users,
  Gift,
  Activity,
  User,
  Settings,
  Info,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  TrendingUp,
  Target,
  BarChart3,
  Copy,
  Share2,
} from "lucide-react";
import {
  usePromotion,
  useTogglePromotionStatus,
  getPromotionStatus,
  isPromotionCurrentlyValid,
} from "@/api-services/hooks/usePromotions";
import {
  Promotion,
  DISCOUNT_TYPES,
  USER_TYPES,
} from "@/api-services/hooks/promotionsTypes";

const ViewPromotionPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const promotionId = parseInt(id || "0");

  // State for managing toggle confirmation
  const [isToggling, setIsToggling] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Fetch promotion details
  const { data: promotion, loading, error, refetch } = usePromotion(
    promotionId,
    {
      enabled: !!promotionId && promotionId > 0,
      onError: (err) => {
        toast.error(`Failed to load promotion details: ${err.message}`);
      },
    }
  );

  // Toggle promotion status hook
  const { mutate: toggleStatus } = useTogglePromotionStatus({
    onSuccess: (data, promotionId) => {
      setIsToggling(false);
      toast.success("Promotion status updated successfully!");
      refetch(); // Refresh the data
    },
    onError: (error, promotionId) => {
      setIsToggling(false);
      toast.error(`Failed to update promotion status: ${error.message}`);
    },
  });

  // Format currency display
  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  }, []);

  // Format date
  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  // Format date with time
  const formatDateTime = useCallback((dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  }, []);

  // Get discount display value
  const getDiscountDisplay = useCallback((promotion: Promotion) => {
    if (promotion.discountType === 'PERCENTAGE') {
      return `${promotion.discountValue}%`;
    } else {
      return formatCurrency(promotion.discountValue);
    }
  }, [formatCurrency]);

  // Get discount type display
  const getDiscountTypeDisplay = useCallback((discountType: string) => {
    const type = DISCOUNT_TYPES.find(t => t.value === discountType);
    return type ? type.label : discountType;
  }, []);

  // Get user type display
  const getUserTypeDisplay = useCallback((userType: string) => {
    const type = USER_TYPES.find(t => t.value === userType);
    return type ? type.label : userType;
  }, []);

  // Get status badge
  const getStatusBadge = useCallback((promotion: Promotion) => {
    const status = getPromotionStatus(promotion);

    switch (status) {
      case 'active':
        return {
          variant: "default" as const,
          className: "bg-green-100 text-green-800 border-green-200",
          label: "Active",
          icon: CheckCircle
        };
      case 'scheduled':
        return {
          variant: "secondary" as const,
          className: "bg-blue-100 text-blue-800 border-blue-200",
          label: "Scheduled",
          icon: Clock
        };
      case 'expired':
        return {
          variant: "outline" as const,
          className: "bg-gray-100 text-gray-800 border-gray-200",
          label: "Expired",
          icon: XCircle
        };
      case 'inactive':
        return {
          variant: "secondary" as const,
          className: "bg-red-100 text-red-800 border-red-200",
          label: "Inactive",
          icon: AlertCircle
        };
      default:
        return {
          variant: "secondary" as const,
          className: "bg-muted text-muted-foreground",
          label: "Unknown",
          icon: AlertCircle
        };
    }
  }, []);

  // Get discount type badge
  const getDiscountTypeBadge = useCallback((discountType: string) => {
    return discountType === 'PERCENTAGE'
      ? {
          variant: "default" as const,
          className: "bg-purple-100 text-purple-800 border-purple-200",
          label: "Percentage"
        }
      : {
          variant: "default" as const,
          className: "bg-indigo-100 text-indigo-800 border-indigo-200",
          label: "Fixed Amount"
        };
  }, []);

  // Calculate usage percentage
  const getUsagePercentage = useCallback((promotion: Promotion) => {
    if (promotion.maxUsesPerUser <= 0) return 0;
    return Math.min((promotion.totalRedemptions / promotion.maxUsesPerUser) * 100, 100);
  }, []);

  // Copy promo code to clipboard
  const copyPromoCode = useCallback(async (code: string) => {
    try {
      // Validate the promo code
      if (!code || typeof code !== 'string') {
        toast.error("No promo code available to copy");
        return;
      }

      // Fallback for browsers that don't support navigator.clipboard
      if (!navigator.clipboard) {
        // Create a temporary textarea element
        const textArea = document.createElement("textarea");
        textArea.value = code.trim();
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);

          if (successful) {
            setCopiedCode(true);
            toast.success("Promo code copied to clipboard!");
            setTimeout(() => setCopiedCode(false), 2000);
          } else {
            toast.error("Failed to copy promo code");
          }
        } catch (fallbackErr) {
          document.body.removeChild(textArea);
          toast.error("Failed to copy promo code");
        }
        return;
      }

      // Modern browsers with clipboard API support
      await navigator.clipboard.writeText(code.trim());
      setCopiedCode(true);
      toast.success("Promo code copied to clipboard!");
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      toast.error("Failed to copy promo code");
    }
  }, []);

  // Handle status toggle
  const handleToggleStatus = () => {
    if (!promotion) return;

    setIsToggling(true);
    toggleStatus(promotion.promoCodeId);
  };

  // Handle navigation
  const handleEdit = () => {
    navigate(`/admin/promotions/edit/${promotionId}`);
  };

  const handleBack = () => {
    navigate("/admin/promotions");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading promotion details...</p>
        </div>
      </div>
    );
  }

  if (error || !promotion) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Promotion
            </h3>
            <p className="text-muted-foreground mb-4">
              {error?.message || "Promotion not found"}
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => refetch()}>
                Try Again
              </Button>
              <Button onClick={handleBack}>Back to List</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusBadge = getStatusBadge(promotion);
  const discountTypeBadge = getDiscountTypeBadge(promotion.discountType);
  const StatusIcon = statusBadge.icon;
  const isValid = isPromotionCurrentlyValid(promotion);
  const usagePercentage = getUsagePercentage(promotion);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Promotions
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Promotion Details
            </h1>
            <p className="text-muted-foreground mt-1">
              View detailed information about this promotional offer
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleToggleStatus}
            disabled={isToggling}
          >
            {promotion.isActive ? (
              <>
                <ToggleLeft className="w-4 h-4 mr-2" />
                Deactivate
              </>
            ) : (
              <>
                <ToggleRight className="w-4 h-4 mr-2" />
                Activate
              </>
            )}
          </Button>
          <Button onClick={handleEdit}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Promotion
          </Button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Promotion Code Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Promotion Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Promo Code */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Promo Code
                </label>
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-purple-700">
                      {promotion.promoCode}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyPromoCode(promotion?.promoCode || '')}
                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                  >
                    {copiedCode ? (
                      <CheckCircle className="w-4 h-4 mr-1" />
                    ) : (
                      <Copy className="w-4 h-4 mr-1" />
                    )}
                    {copiedCode ? "Copied!" : "Copy"}
                  </Button>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Description
                </label>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm leading-relaxed">
                    {promotion.description}
                  </p>
                </div>
              </div>

              {/* Location and Vehicle */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    City
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-semibold">
                      {promotion.city?.cityName || "All Cities"}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Car className="w-4 h-4" />
                    Vehicle Category
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-semibold">
                      Vehicle ID: {promotion.vehicleCategoryId}
                    </p>
                  </div>
                </div>
              </div>

              {/* User Type and Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Applicable User Type
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-semibold">
                      {getUserTypeDisplay(promotion.applicableUserType)}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Status
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <Badge
                      className={statusBadge.className}
                    >
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusBadge.label}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Discount Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Discount Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Discount Type and Value */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Discount Type
                  </label>
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                    <Badge className={discountTypeBadge.className}>
                      {discountTypeBadge.label}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Discount Value
                  </label>
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">
                      {getDiscountDisplay(promotion)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Maximum Discount Amount */}
              {promotion.maxDiscountAmount > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Maximum Discount Amount
                  </label>
                  <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg">
                    <p className="text-xl font-bold text-orange-700">
                      {formatCurrency(promotion.maxDiscountAmount)}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Usage Statistics Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Usage Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Total Redemptions
                  </label>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700">
                      {promotion.totalRedemptions}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Maximum Uses Per User
                  </label>
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-2xl font-bold text-purple-700">
                      {promotion.maxUsesPerUser}
                    </p>
                  </div>
                </div>
              </div>

              {/* Usage Progress */}
              {promotion.maxUsesPerUser > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-muted-foreground">
                      Usage Progress
                    </label>
                    <span className="text-sm font-semibold">
                      {usagePercentage.toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={usagePercentage} className="h-2" />
                  <p className="text-xs text-muted-foreground">
                    {promotion.totalRedemptions} of {promotion.maxUsesPerUser} uses
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* System Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                System Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Created By
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-medium">
                      {promotion.createdBy || "Unknown"}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Promotion ID
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-mono text-sm">
                      #{promotion.promoCodeId}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Created Date
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-sm">
                      {formatDateTime(promotion.createdDate)}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Last Modified
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-sm">
                      {formatDateTime(promotion.modifiedDate)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Status & Actions */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Promotion Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <Gift className="w-8 h-8 text-white" />
                </div>
                <Badge
                  variant={statusBadge.variant}
                  className={
                    `${statusBadge.className} text-sm px-3 py-1`
                  }
                >
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusBadge.label}
                </Badge>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Currently Valid:</span>
                  <span className={isValid ? "text-green-600" : "text-red-600"}>
                    {isValid ? "Yes" : "No"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deleted:</span>
                  <span>{promotion.isDeletedValue ? "Yes" : "No"}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Validity Period Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Validity Period
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">Valid From</p>
                  <p className="font-semibold text-green-600">
                    {formatDate(promotion.validFrom)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">Valid Until</p>
                  <p className="font-semibold text-red-600">
                    {formatDate(promotion.validTo)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={handleEdit}
                className="w-full"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Promotion
              </Button>
              <Button
                variant="outline"
                onClick={handleToggleStatus}
                disabled={isToggling}
                className="w-full"
              >
                {promotion.isActive ? (
                  <>
                    <ToggleLeft className="w-4 h-4 mr-2" />
                    Deactivate Promotion
                  </>
                ) : (
                  <>
                    <ToggleRight className="w-4 h-4 mr-2" />
                    Activate Promotion
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => copyPromoCode(promotion?.promoCode || '')}
                className="w-full"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Promo Code
              </Button>
              <Button
                variant="outline"
                onClick={handleBack}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to List
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ViewPromotionPage;